import React, { useEffect, useMemo, useState } from "react";
import { HiOutlineBookmark, HiOutlineCheck, HiOutlineSparkles } from "react-icons/hi";
import ThemedDropdown from "components/common/DropDown/ThemedDropdown";
import BulkAnalyseSearchSelect from "./BulkAnalyseSearchSelect";
import { addMonths, capitalizeAllLetters } from "Utils/utils";
import TimesheetFilterDateRange from "../../components/TimesheetRevamp/TimesheetFilterDateRange";
import TimesheetFilterField from "../../components/TimesheetRevamp/TimesheetFilterField";
import { useBulkAnalyse } from "../BulkAnalyseContext";
import { formatRangeLabel } from "../model/bulkAnalyseModel";
import BulkAnalyseKpi from "./BulkAnalyseKpi";
import { BulkAnalyseTable } from "./BulkAnalyseTable";
import {
  BulkAnalyseFooter,
  BulkAnalyseModalChrome,
  BulkPrimaryButton,
  BulkSecondaryButton,
} from "./BulkAnalyseModalChrome";

const MAX_RANGE_DAYS = 93;

const localDate = (value) => {
  if (!value) return null;
  const [year, month, day] = String(value).split("-").map(Number);
  if (!year || !month || !day) return null;
  return new Date(year, month - 1, day);
};

const rangeDayCount = (from, to) => {
  const start = localDate(from);
  const end = localDate(to);
  if (!start || !end) return 0;
  return Math.round((end.getTime() - start.getTime()) / 86400000);
};

function Bone({ className = "" }) {
  return <span className={`inline-block animate-pulse rounded bg-[#E2E8F0] ${className}`} />;
}

function SummaryFigure({ value, label, hint, loading }) {
  return (
    <div className="flex min-w-0 flex-1 flex-col justify-center gap-0.5 px-4 py-2.5 sm:px-4 sm:py-0">
      <p className="text-[22px] font-bold leading-none text-[#0F172A]">
        {loading ? <Bone className="h-6 w-16" /> : value}
      </p>
      <p className="text-[13px] font-semibold leading-none text-[#334155]">{label}</p>
      {hint ? <p className="text-xs font-medium leading-none text-[#64748B]">{hint}</p> : null}
    </div>
  );
}

function Stepper({ step }) {
  const filtersDone = step === 2;
  const pill = (active, done) =>
    done
      ? "border-[#A7F3D0] bg-[#F0FDF4]"
      : active
        ? "border-[#4B6CB7] bg-[#EEF2FB]"
        : "border-[#E5E7EB] bg-white";
  const mark = (active, done) =>
    done
      ? "bg-[#059669] text-white"
      : active
        ? "bg-[#4B6CB7] text-white"
        : "bg-[#F1F5F9] text-[#64748B]";
  const item = (number, label, hint, hintClass, active, done) => (
    <div
      className={`flex h-[50px] min-h-[50px] min-w-0 flex-1 items-center gap-3 rounded-[14px] border px-3 sm:px-[18px] ${pill(active, done)}`}
      style={{ height: 50, minHeight: 50 }}
    >
      <span
        className={`inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-sm font-bold ${mark(active, done)}`}
      >
        {done ? <HiOutlineCheck className="h-4 w-4" strokeWidth={2.5} /> : number}
      </span>
      <span className="min-w-0">
        <span className="block text-[12px] font-semibold leading-tight text-[#0F172A]">{label}</span>
        {hint ? (
          <span className={`mt-0.5 block text-xs font-medium ${hintClass}`}>{hint}</span>
        ) : null}
      </span>
    </div>
  );
  return (
    <div className="flex flex-col gap-2 sm:flex-row sm:gap-3">
      {item(
        1,
        "Filters",
        filtersDone ? "Set chosen" : "Choose the timesheets",
        filtersDone ? "text-[#047857]" : "text-[#4B6CB7]",
        step === 1,
        filtersDone
      )}
      {item(
        2,
        "Summary",
        step === 2 ? "Review and run" : "Totals, then run",
        step === 2 ? "text-[#4B6CB7]" : "text-[#64748B]",
        step === 2,
        false
      )}
    </div>
  );
}

function Notice({ tone = "warning", children }) {
  const styles =
    tone === "error"
      ? "border-[#FECACA] bg-[#FEF2F2] text-[#B91C1C]"
      : "border-[#FDE68A] bg-[#FFFBEB] text-[#92400E]";
  return (
    <p role="alert" className={`rounded-lg border px-3 py-2 text-[13px] font-medium ${styles}`}>
      {children}
    </p>
  );
}

const BulkAnalyseConfirmModal = () => {
  const {
    confirmOpen,
    closeConfirm,
    step,
    setStep,
    filters,
    setFilter,
    presetLabel,
    scope,
    confirmPage,
    page,
    setPage,
    options,
    balance,
    previewLoading,
    countsLoading,
    previewError,
    onOpenReport,
    createLoading,
    createError,
    beginRun,
  } = useBulkAnalyse();

  const subtitle =
    step === 1 ? "Step 1 of 2 · Filters" : "Step 2 of 2  ·  Summary";
  const modalTitle = step === 2 ? "Confirm AI Analyse" : "Bulk Analyse";
  const [selectedFromDate, setSelectedFromDate] = useState(null);

  useEffect(() => {
    if (confirmOpen) setSelectedFromDate(null);
  }, [confirmOpen]);

  const rangeFrom = useMemo(() => localDate(filters.from), [filters.from]);
  const rangeTo = useMemo(() => localDate(filters.to), [filters.to]);
  const rangeMaxDate = useMemo(() => {
    const anchor = selectedFromDate || new Date();
    return addMonths(new Date(anchor.getFullYear(), anchor.getMonth(), anchor.getDate()), 3);
  }, [selectedFromDate]);
  const rangeTooLong = rangeDayCount(filters.from, filters.to) > MAX_RANGE_DAYS;
  const canContinue =
    scope.willRun > 0 && !scope.tooMany && !rangeTooLong && !previewLoading && !previewError;
  const workerOptions = [
    { value: "all", label: "All workers" },
    ...(options.workers || []).map((worker) => ({
      value: String(worker.id),
      label: capitalizeAllLetters(worker.name),
    })),
  ];
  const participantOptions = [
    { value: "all", label: "All participants" },
    ...(options.participants || []).map((participant) => ({
      value: String(participant.id),
      label: capitalizeAllLetters(participant.name),
    })),
  ];
  const inlineError =
    createError && createError.code !== "BULK_RUN_ACTIVE" ? createError.message : "";
  const rangeDays = Math.max(0, rangeDayCount(filters.from, filters.to) + 1);
  const rangeCaption = formatRangeLabel(filters.from, filters.to) || presetLabel || "these dates";

  return (
    <BulkAnalyseModalChrome
      isOpen={confirmOpen}
      onClose={closeConfirm}
      title={modalTitle}
      subtitle={subtitle}
      tallHeader={step === 2}
      fillHeight={step === 1}
      wide
      panelClassName={
        step === 2
          ? "!w-[75vw] !max-w-[75vw] rounded-[20px]"
          : "!w-[75vw] !max-w-[75vw] rounded-2xl"
      }
      footer={
        <BulkAnalyseFooter tall={step === 2}>
          <div className={`ml-auto flex items-center justify-end gap-2 ${step === 2 ? "shrink-0" : "w-full flex-wrap lg:w-auto lg:shrink-0"}`}>
            {step === 1 ? (
              <BulkSecondaryButton onClick={closeConfirm}>Cancel</BulkSecondaryButton>
            ) : (
              <BulkSecondaryButton className="w-[104px]" onClick={() => setStep(1)} disabled={createLoading}>
                Back
              </BulkSecondaryButton>
            )}
            {step === 1 ? (
              <BulkPrimaryButton onClick={() => setStep(2)} disabled={!canContinue}>
                Next
              </BulkPrimaryButton>
            ) : (
              <BulkPrimaryButton
                className="!px-4"
                icon={createLoading ? null : <HiOutlineSparkles className="h-4 w-4" />}
                onClick={beginRun}
                disabled={!canContinue || createLoading}
              >
                {createLoading
                  ? "Starting…"
                  : scope.willRun === 1
                    ? "Analyse 1"
                    : `Analyse all ${scope.willRun}`}
              </BulkPrimaryButton>
            )}
          </div>
        </BulkAnalyseFooter>
      }
    >
      <div className={`flex min-h-0 min-w-0 flex-col ${step === 2 ? "gap-2 overflow-visible px-4 pb-4 pt-4 sm:gap-3 sm:px-6 sm:pt-4" : "flex-1 gap-3 overflow-hidden px-4 py-3 sm:px-6"}`}>
        <div className="shrink-0">
          <div className="flex flex-col gap-3">
            <Stepper step={step} />
            <div className={`grid grid-cols-2 ${step === 2 ? "gap-2 lg:grid-cols-4" : "gap-2 sm:gap-3 xl:grid-cols-4"}`}>
              <BulkAnalyseKpi compact={step === 2} label="Pending timesheets" value={scope.pending} caption={step === 2 ? `Of ${scope.matched} in this period` : "Pending in these filters"} accent="bg-[#F59E0B]" loading={countsLoading} />
              <BulkAnalyseKpi compact={step === 2} label="Match these filters" value={scope.matched} caption={step === 2 ? "Worker, participant, date" : "Worker, participant, dates"} accent="bg-[#4B6CB7]" loading={countsLoading} />
              <BulkAnalyseKpi
                label="Already analysed"
                value={scope.alreadyAnalysed}
                caption={filters.leaveOutAlreadyAnalysed ? "Excluded" : "Included"}
                accent={step === 2 ? "bg-[#0DBBB3]" : "bg-[#94A3B8]"}
                loading={countsLoading}
                compact={step === 2}
              />
              <BulkAnalyseKpi compact={step === 2} label="Will run" value={scope.willRun} caption={step === 2 ? "Not analysed yet" : "Will be analysed"} accent={step === 2 ? "bg-[#4A6BB6]" : "bg-[#0DBBB3]"} loading={countsLoading} />
            </div>
          </div>
        </div>
        {step === 1 ? (
          <>
            <div className="grid shrink-0 grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4 [&_.themed-dropdown-trigger]:w-full">
              <TimesheetFilterField label="Worker">
                <BulkAnalyseSearchSelect
                  value={filters.worker}
                  options={workerOptions}
                  placeholder="All workers"
                  searchPlaceholder="Search workers..."
                  emptyLabel="No workers found"
                  onChange={(value) => setFilter({ worker: value })}
                />
              </TimesheetFilterField>
              <TimesheetFilterField label="Participant">
                <BulkAnalyseSearchSelect
                  value={filters.participant}
                  options={participantOptions}
                  placeholder="All participants"
                  searchPlaceholder="Search participants..."
                  emptyLabel="No participants found"
                  onChange={(value) => setFilter({ participant: value })}
                />
              </TimesheetFilterField>
              <TimesheetFilterField label="Date range">
                <TimesheetFilterDateRange
                  fromDate={rangeFrom}
                  toDate={rangeTo}
                  handleChange={({ fromDate, toDate }) =>
                    setFilter({ from: fromDate, to: toDate, presetId: "" })
                  }
                  setSelectedFromDate={setSelectedFromDate}
                  maxDate={rangeMaxDate}
                  portalId="bulk-analyse-date-portal"
                  fullWidth
                />
              </TimesheetFilterField>
              <TimesheetFilterField label="Already analysed">
                <ThemedDropdown
                  anchored
                  value={filters.leaveOutAlreadyAnalysed ? "exclude" : "include"}
                  options={[
                    { value: "include", label: "Include" },
                    { value: "exclude", label: "Exclude" },
                  ]}
                  onChange={(value) =>
                    setFilter({ leaveOutAlreadyAnalysed: value === "exclude" })
                  }
                  textClass="text-[13px] font-semibold"
                />
              </TimesheetFilterField>
            </div>
            {previewError ? <Notice tone="error">{previewError}</Notice> : null}
            {rangeTooLong ? (
              <Notice>
                {`Choose a date range of ${MAX_RANGE_DAYS} days or less.`}
              </Notice>
            ) : null}
            {scope.tooMany ? (
              <Notice>
                {`There are ${scope.willRun} ${scope.willRun === 1 ? "timesheet" : "timesheets"} in this list. Bulk Analyse can take ${scope.maxTimesheets} at a time. Change the filters until ${scope.maxTimesheets} or fewer are left.`}
              </Notice>
            ) : null}
            <BulkAnalyseTable
              className="min-h-0 flex-1"
              rows={confirmPage.rows}
              page={page}
              totalPages={confirmPage.totalPages}
              total={confirmPage.total}
              onPageChange={setPage}
              onOpenReport={onOpenReport}
              loading={previewLoading}
            />
          </>
        ) : (
          <>
            {inlineError ? <Notice tone="error">{inlineError}</Notice> : null}
            <div className="flex flex-col gap-2">
            <section className="overflow-hidden rounded-2xl border border-[#E5E7EB] bg-white">
              <div className="flex h-10 items-center border-b border-[#E5E7EB] bg-[#F8FAFC] px-4">
                <h3 className="min-w-0 truncate text-[13px] font-semibold text-[#0F172A]">
                  These timesheets will be analysed together. A credit is used only when each one finishes.
                </h3>
              </div>
              <div className="grid grid-cols-1 divide-y divide-[#E5E7EB] sm:h-[76px] sm:grid-cols-3 sm:divide-x sm:divide-y-0">
                <SummaryFigure
                  value={scope.willRun}
                  label="Timesheets to analyse"
                  hint="Included in this analysis"
                  loading={countsLoading}
                />
                <SummaryFigure
                  value={rangeDays > 0 ? `${rangeDays} ${rangeDays === 1 ? "day" : "days"}` : "—"}
                  label="Date range"
                  hint={rangeCaption}
                  loading={countsLoading}
                />
                <SummaryFigure
                  value={balance != null ? `$${Number(balance).toFixed(2)}` : "—"}
                  label="Credit balance"
                  hint="Taken only after a timesheet finishes"
                  loading={countsLoading}
                />
              </div>
            </section>
            <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
              <article className="flex items-center gap-3 rounded-2xl border border-[#E5E7EB] bg-[#EEF2FB] px-3.5 py-2 sm:px-4">
                <span className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-[#4B6CB7] text-white">
                  <HiOutlineBookmark className="h-4 w-4" />
                </span>
                <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                  <p className="text-[13px] font-bold leading-none text-[#0F172A]">Saved on this page</p>
                  <p className="text-xs font-medium leading-snug text-[#475569]">
                    Leave or refresh. The job stays on Rostering / Timesheet.
                  </p>
                </div>
              </article>
              <article className="flex items-center gap-3 rounded-2xl border border-[#E5E7EB] bg-[#E8F8F1] px-3.5 py-2 sm:px-4">
                <span className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-[#12B981] text-white">
                  <HiOutlineCheck className="h-4 w-4" />
                </span>
                <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                  <p className="text-[13px] font-bold leading-none text-[#0F172A]">You still approve</p>
                  <p className="text-xs font-medium leading-snug text-[#475569]">
                    AI writes the score. Approve and Reject stay yours.
                  </p>
                </div>
              </article>
            </div>
            </div>
          </>
        )}
      </div>
    </BulkAnalyseModalChrome>
  );
};

export default BulkAnalyseConfirmModal;
